import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '../store/uiStore';
import { initializeTenantConfig } from '../services/tenantService';
import type { TenantConfig } from '../types/tenant';
import type { ParentAppContext } from '../types/parentContext';
import { isJWT, extractJwtContext } from '../services/jwtContextService';
import { getMaintenanceStatus } from '../services/api/endpoints/maintenanceEndpoints';
import { logger } from '../utils/logger';

interface EmbeddedAppContextType {
  parentContext: ParentAppContext | null;
  isLoading: boolean;
  error: string | null;
  isEmbedded: boolean;
  cacheExpired: boolean;
  sendMessageToParent: (message: any) => void;
  requestRefresh: () => void;
  tenantConfig: TenantConfig | null;
}

const EmbeddedAppContext = createContext<EmbeddedAppContextType | undefined>(undefined);

export const useEmbeddedApp = () => {
  const context = useContext(EmbeddedAppContext);
  if (context === undefined) {
    throw new Error('useEmbeddedApp must be used within an EmbeddedAppProvider');
  }
  return context;
};

interface EmbeddedAppProviderProps {
  children: ReactNode;
}

export const EmbeddedAppProvider: React.FC<EmbeddedAppProviderProps> = ({ children }) => {
  const [parentContext, setParentContext] = useState<ParentAppContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
  const { i18n } = useTranslation();
  const { setLanguage } = useUIStore();

  // Use ref to track the latest parentContext without causing re-renders
  const parentContextRef = useRef<ParentAppContext | null>(null);

  // Ref to track loading state for timeout closure
  const isLoadingRef = useRef<boolean>(true);

  // Ref to store timeout ID for cleanup
  const contextTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    parentContextRef.current = parentContext;
  }, [parentContext]);

  // Sync isLoading ref with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Initialize tenant configuration (will be called when parentContext is available)
  const initializeTenant = React.useCallback(async (contextTenantId?: string) => {
    try {
      const config = await initializeTenantConfig(contextTenantId);
      setTenantConfig(config);
    } catch (error) {
      logger.error('Failed to initialize tenant config', {
        errorType: 'TenantConfigError'
      }, error as Error);
      // Tenant service already applied the default branding
      // No need to throw error here, app will continue with defaults
    }
  }, []);

  // Note: Tenant initialization removed from mount - will be called when CONTEXT_RESPONSE is received
  // This prevents calling /api/tenants/resolve-origin with origin=null before context is available

  // Check if app is running in embedded context (iframe/WebView)
  useEffect(() => {
    const checkEmbeddedContext = () => {
      try {
        // Check if we're in an iframe
        const inIframe = window.self !== window.top;

        // Check for WebView context (common mobile indicators)
        const inWebView = (
          /wv/i.test(navigator.userAgent) || // Android WebView
          /Version.*Mobile.*Safari/.test(navigator.userAgent) || // iOS WebView
          window.ReactNativeWebView !== undefined || // React Native WebView
          window.flutter_inappwebview !== undefined // Flutter WebView
        );

        setIsEmbedded(inIframe || inWebView);

        if (inIframe || inWebView) {
          // Delay sending APP_READY to ensure message handler is registered
          // This prevents race condition where parent sends CONTEXT_RESPONSE before we're ready
          setTimeout(() => {
            // Send ready signal to parent
            // SECURITY: Use specific origin instead of '*' when possible
            const parentOrigin = import.meta.env.VITE_PARENT_APP_ORIGIN || '*';
            if (window.parent && window.parent !== window) {
              window.parent.postMessage({
                type: 'APP_READY',
                timestamp: new Date().toISOString()
              }, parentOrigin);
            }
            // Request context from parent app
            requestContextFromParent();
          }, 100); // Small delay to ensure message handler is registered
        } else {
          // Running standalone - wait for parent context
          // No mock data - must be provided by parent app
          console.warn('[EmbeddedAppProvider] Running in standalone mode - waiting for parent context');
          setIsLoading(true);
          setError('Application must be embedded in parent app');
        }
      } catch (e) {
        logger.error('Error checking embedded context', {
          errorType: 'EmbeddedContextCheckError'
        }, e as Error);
        setIsLoading(false);
        setError('Failed to detect embedded context');
      }
    };

    checkEmbeddedContext();
  }, []);

  // Listen for messages from parent application
  useEffect(() => {
    // Allowed origins for PostMessage communication
    const ALLOWED_ORIGINS = [
      'https://app.croixbleue.ca',
      'https://app-staging.croixbleue.ca',
      'https://app-dev.croixbleue.ca',
      'https://claims.canassistance.com',
      'https://claims-staging.canassistance.com',
      // Development and staging environments (*.svcnp.canassurance.com)
      'https://claims-portal-frontend-web-dev.svcnp.canassurance.com',
      'https://claims-portal-frontend-web-staging.svcnp.canassurance.com',
      ...(import.meta.env.DEV ? ['http://localhost:5173', 'http://localhost:5174'] : [])
    ];

    const handleMessage = (event: MessageEvent) => {
      // SECURITY: Origin validation temporarily disabled - TODO: Re-enable in production
      // Temporary: Allow all origins (SECURITY WARNING - re-enable validation later)
      // if (!import.meta.env.DEV) {
      //   const isAllowed = ALLOWED_ORIGINS.some(origin =>
      //     event.origin === origin || event.origin.startsWith(origin)
      //   );
      //   if (!isAllowed) {
      //     console.warn('[EmbeddedAppProvider] Rejected message from unauthorized origin:', event.origin);
      //     return;
      //   }
      // }

      // Call async handler (event listeners can't be async directly)
      handleMessageAsync(event).catch(error => {
        logger.error('Error in async message handler', {
          errorType: 'MessageHandlerError'
        }, error as Error);
        setError('Failed to process parent app message');
        setIsLoading(false);
      });
    };

    const handleMessageAsync = async (event: MessageEvent) => {
      try {
        const message = event.data;

        switch (message.type) {
          case 'CONTEXT_RESPONSE':
            let context = message.context || message.payload;

            // Extract tenantId from message root level (for passing to extractJwtContext)
            const messageTenantId = message.tenantId || context?.tenantId;

            // Maintenance gate (DEV-6343): the status probe is public/no-auth — check it BEFORE the
            // JWT context-extraction handshake, so we never attempt a business bootstrap call (e.g.
            // /api/v1/jwt/extract-context) while the portal is in maintenance. The tenant is already
            // known here and is sent as X-Tenant-ID.
            try {
              const maintenanceStatus = await getMaintenanceStatus(messageTenantId);
              if (maintenanceStatus.maintenance) {
                // Minimal context so the maintenance-page polling probe carries X-Tenant-ID for this
                // tenant (the full handshake — incl. extract-context — is skipped while in maintenance).
                (window as any).parentContext = {
                  ...(window as any).parentContext,
                  tenantId: messageTenantId,
                };
                // Apply tenant branding so the maintenance page uses the tenant's colours (the rest of
                // the handshake is skipped here). Uses the localStorage cache when present; otherwise
                // fetches /api/tenants/{id}/config — exempt from the maintenance 503 filter. Falls back
                // to default branding on failure (handled inside initializeTenant).
                if (messageTenantId) {
                  await initializeTenant(messageTenantId);
                }
                useUIStore.getState().setMaintenance(true, maintenanceStatus.pollIntervalMs);
                setIsLoading(false);
                return;
              }
            } catch {
              // Probe unreachable — proceed; the axios 503 interceptor + maintenance gate are the safety net.
            }

            // CASE 1: Context is a JWT string directly (e.g., context: "eyJhbG...")
            // JWT contains ONLY confidential data (clientInfo, contracts, bankingInfo)
            // Application metadata (clientId, tenantId, etc.) must be provided separately
            if (typeof context === 'string' && isJWT(context)) {
              console.warn('[EmbeddedAppProvider] ⚠️ WARNING: JWT-only context detected. Application metadata (clientId, tenantId, language) missing!');

              if (!messageTenantId) {
                logger.error('No tenantId in message - cannot validate JWT without tenant context', {
                  errorType: 'MissingTenantId',
                  messageType: 'CONTEXT_RESPONSE'
                });
                setError('Missing tenant ID in context message');
                setIsLoading(false);
                return;
              }

              try {
                const jwtConfidentialData = await extractJwtContext(context, messageTenantId);
                context = {
                  // JWT confidential data (validated by backend)
                  ...jwtConfidentialData,
                  // Keep original token for API calls
                  token: context,
                  // Default values for missing application metadata (should be provided separately!)
                  clientId: 'unknown',
                  tenantId: messageTenantId, // Use tenant ID from message
                  cacheTimestamp: jwtConfidentialData.cacheTimestamp || Date.now(),
                };
                console.warn('[EmbeddedAppProvider] ⚠️ Using default values for missing application metadata');
              } catch (error: any) {
                logger.error('Backend JWT validation failed', {
                  errorType: 'JWTValidationError',
                  messageType: 'CONTEXT_RESPONSE'
                }, error);
                setError('Invalid or expired JWT token received from parent app');
                setIsLoading(false);
                return;
              }
            }
            // CASE 2: Context object with JWT token - ALWAYS extract from JWT (default behavior)
            else if (context?.token && isJWT(context.token)) {
              // Use tenantId from context or message root
              const contextTenantId = context.tenantId || messageTenantId;
              if (!contextTenantId) {
                logger.error('No tenantId in context - cannot validate JWT without tenant context', {
                  errorType: 'MissingTenantId',
                  messageType: 'CONTEXT_RESPONSE'
                });
                setError('Missing tenant ID in context');
                setIsLoading(false);
                return;
              }

              try {
                const jwtConfidentialData = await extractJwtContext(context.token, contextTenantId);

                // Merge JWT confidential data with context application metadata
                context = {
                  // Application metadata (from parent context - NOT from JWT)
                  clientId: context.clientId,
                  tenantId: context.tenantId,
                  language: context.language,
                  corporateClientName: context.corporateClientName,
                  token: context.token,
                  // Confidential data (from JWT, validated by backend) - ALWAYS from backend
                  clientInfo: jwtConfidentialData.clientInfo,
                  contracts: jwtConfidentialData.contracts,
                  bankingInfo: jwtConfidentialData.bankingInfo,
                  issuedAt: jwtConfidentialData.issuedAt,
                  expiresAt: jwtConfidentialData.expiresAt,
                  cacheTimestamp: context.cacheTimestamp || jwtConfidentialData.cacheTimestamp || Date.now(),
                };
              } catch (error: any) {
                logger.error('Backend JWT validation failed', {
                  errorType: 'JWTValidationError',
                  messageType: 'CONTEXT_RESPONSE'
                }, error);
                setError('Invalid or expired JWT token in context');
                setIsLoading(false);
                return;
              }
            }
            // CASE 3: Context without JWT token (legacy mode) - use data as-is

            // Clear timeout since context arrived successfully
            if (contextTimeoutRef.current) {
              clearTimeout(contextTimeoutRef.current);
              contextTimeoutRef.current = null;
            }

            setParentContext(context);

            // Also set window.parentContext for API endpoints
            // Fixed: Support both contracts and contractInfo (legacy) fields
            const contractsArray = context.contracts || (context as any).contractInfo || [];
            (window as any).parentContext = {
              jwt: context.token,
              tenantId: context.tenantId, // CRITICAL: Required for X-Tenant-ID header in API calls
              corporateClientName: context.clientInfo?.corporateClientName || context.corporateClientName,
              contractNumbers: contractsArray.map((c: any) => c.contractNumber) || [],
              locale: context.language || 'fr', // Use root language field only
            };

            // If context includes tenantId, reinitialize tenant configuration
            if (context?.tenantId) {
              initializeTenant(context.tenantId);
            }

            // Appliquer la langue du contexte parent (only if different from current)
            // Use ONLY root language field (not clientInfo.language)
            if (context?.language) {
              const lang = context.language;
              const currentLang = useUIStore.getState().language;
              if (lang !== currentLang) {
                setLanguage(lang);
              }
            }

            setIsLoading(false);
            setError(null);
            break;

          case 'CONTEXT_ERROR':
            setError(message.error || 'Failed to load context from parent app');
            setIsLoading(false);
            break;

          case 'TOKEN_REFRESH':
            if (parentContextRef.current) {
              const refreshedContext = {
                ...parentContextRef.current,
                token: message.payload.token,
                cacheTimestamp: Date.now()
              };
              setParentContext(refreshedContext);

              // Update JWT and tenantId in window.parentContext
              if ((window as any).parentContext) {
                (window as any).parentContext.jwt = message.payload.token;
                (window as any).parentContext.tenantId = refreshedContext.tenantId; // Preserve tenantId
              }
            }
            break;

          case 'CONTEXT_UPDATED':
            let updatedContext = message.payload;

            // Extract tenantId from message root level or payload (for passing to extractJwtContext)
            const updateTenantId = message.tenantId || updatedContext?.tenantId || parentContextRef.current?.tenantId;

            // CASE 1: Payload is a JWT string directly
            // JWT contains ONLY confidential data (clientInfo, contracts, bankingInfo)
            if (typeof updatedContext === 'string' && isJWT(updatedContext)) {
              console.warn('[EmbeddedAppProvider] ⚠️ WARNING: JWT-only CONTEXT_UPDATED. Application metadata missing!');

              if (!updateTenantId) {
                logger.error('No tenantId for CONTEXT_UPDATED - cannot validate JWT', {
                  errorType: 'MissingTenantId',
                  messageType: 'CONTEXT_UPDATED'
                });
                break;
              }

              try {
                const jwtConfidentialData = await extractJwtContext(updatedContext, updateTenantId);
                updatedContext = {
                  // JWT confidential data
                  ...jwtConfidentialData,
                  // Keep original token
                  token: updatedContext,
                  // Preserve existing application metadata from current context
                  clientId: parentContextRef.current?.clientId || 'unknown',
                  tenantId: updateTenantId, // Use extracted tenant ID
                  language: parentContextRef.current?.language,
                  corporateClientName: parentContextRef.current?.corporateClientName,
                  cacheTimestamp: jwtConfidentialData.cacheTimestamp || Date.now(),
                };
              } catch (error: any) {
                logger.error('Backend JWT validation failed for CONTEXT_UPDATED', {
                  errorType: 'JWTValidationError',
                  messageType: 'CONTEXT_UPDATED'
                }, error);
                break;
              }
            }
            // CASE 2: Payload is an object with JWT token - ALWAYS extract from JWT (default behavior)
            else if (updatedContext?.token && isJWT(updatedContext.token)) {
              // Use tenantId from update payload or fallback
              const contextUpdateTenantId = updatedContext.tenantId || updateTenantId;
              if (!contextUpdateTenantId) {
                logger.error('No tenantId for CONTEXT_UPDATED - cannot validate JWT', {
                  errorType: 'MissingTenantId',
                  messageType: 'CONTEXT_UPDATED'
                });
                break;
              }

              try {
                const jwtConfidentialData = await extractJwtContext(updatedContext.token, contextUpdateTenantId);

                // Merge JWT confidential data with context application metadata
                updatedContext = {
                  // Application metadata (from update payload - NOT from JWT)
                  clientId: updatedContext.clientId,
                  tenantId: updatedContext.tenantId,
                  language: updatedContext.language,
                  corporateClientName: updatedContext.corporateClientName,
                  token: updatedContext.token,
                  // Confidential data (from JWT, validated by backend) - ALWAYS from backend
                  clientInfo: jwtConfidentialData.clientInfo,
                  contracts: jwtConfidentialData.contracts,
                  bankingInfo: jwtConfidentialData.bankingInfo,
                  issuedAt: jwtConfidentialData.issuedAt,
                  expiresAt: jwtConfidentialData.expiresAt,
                  cacheTimestamp: updatedContext.cacheTimestamp || jwtConfidentialData.cacheTimestamp || Date.now(),
                };
              } catch (error: any) {
                logger.error('Backend JWT validation failed for CONTEXT_UPDATED', {
                  errorType: 'JWTValidationError',
                  messageType: 'CONTEXT_UPDATED'
                }, error);
                break;
              }
            }
            // CASE 3: Context without JWT token (legacy mode) - use data as-is

            // TODO: SECURITY - Remove detailed context logging before production deployment
            setParentContext(updatedContext);

            // Also update window.parentContext for API endpoints
            // Fixed: Support both contracts and contractInfo (legacy) fields
            const updatedContractsArray = updatedContext.contracts || (updatedContext as any).contractInfo || [];
            (window as any).parentContext = {
              jwt: updatedContext.token,
              tenantId: updatedContext.tenantId, // CRITICAL: Required for X-Tenant-ID header in API calls
              corporateClientName: updatedContext.clientInfo?.corporateClientName || updatedContext.corporateClientName,
              contractNumbers: updatedContractsArray.map((c: any) => c.contractNumber) || [],
              locale: updatedContext.language || 'fr', // Use root language field only
            };

            // Mettre à jour la langue si elle a changé (only if different)
            // Use ONLY root language field (not userProfile.language or clientInfo.language)
            if (updatedContext?.language) {
              const lang = updatedContext.language;
              const currentLang = useUIStore.getState().language;
              if (lang !== currentLang) {
                setLanguage(lang);
              }
            }
            break;

          case 'LANGUAGE_CHANGE':
            // Message direct pour changer la langue
            if (message.language) {
              // Normalize language format: 'fr' -> 'FR', 'en' -> 'EN'
              const normalizedLang = message.language.toUpperCase() as 'FR' | 'EN';
              const currentLang = useUIStore.getState().language;

              if (normalizedLang !== currentLang) {
                setLanguage(normalizedLang);
              }
            }
            break;

          default:
            // Unknown message type - ignore silently
            break;
        }
      } catch (e) {
        logger.error('Error handling parent message', {
          errorType: 'MessageHandlingError'
        }, e as Error);
        setError('Invalid message from parent application');
      }
    };

    if (isEmbedded) {
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    }
  }, [isEmbedded, initializeTenant]); // Add initializeTenant to dependencies

  const requestContextFromParent = () => {
    setIsLoading(true);
    setError(null);

    // Clear any existing timeout
    if (contextTimeoutRef.current) {
      clearTimeout(contextTimeoutRef.current);
    }

    // Send request to parent app
    const message = {
      type: 'REQUEST_CONTEXT',
      timestamp: Date.now()
    };

    // SECURITY: Use specific origin instead of '*' when possible
    const parentOrigin = import.meta.env.VITE_PARENT_APP_ORIGIN || '*';

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, parentOrigin);
    } else if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
    } else if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('REQUEST_CONTEXT', message);
    }

    // Set timeout for context loading with proper ref usage
    contextTimeoutRef.current = setTimeout(() => {
      // Check ref value, not closure state
      if (isLoadingRef.current) {
        logger.error('Context request timed out - parent app did not respond', {
          errorType: 'ContextTimeout',
          timeoutMs: 10000
        });
        setError('Timeout waiting for parent app context. Please ensure the application is properly embedded.');
        setIsLoading(false);
        // No fallback to mock data - parent MUST provide context
      }
    }, 10000); // 10 second timeout
  };

  // loadMockContext() REMOVED
  // Application MUST receive context from parent app via PostMessage
  // No mock data fallback - ensures proper integration testing

  const sendMessageToParent = (message: any) => {
    const messageWithTimestamp = {
      ...message,
      timestamp: Date.now()
    };

    // SECURITY: Use specific origin instead of '*' when possible
    const parentOrigin = import.meta.env.VITE_PARENT_APP_ORIGIN || '*';

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(messageWithTimestamp, parentOrigin);
    } else if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(messageWithTimestamp));
    } else if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('PORTAL_MESSAGE', messageWithTimestamp);
    }
  };

  const requestRefresh = () => {
    requestContextFromParent();
  };

  // Calculate if cache is expired (24 hours)
  const cacheExpired = parentContext ? 
    (Date.now() - parentContext.cacheTimestamp) > (24 * 60 * 60 * 1000) : 
    false;

  const contextValue: EmbeddedAppContextType = {
    parentContext,
    isLoading,
    error,
    isEmbedded,
    cacheExpired,
    sendMessageToParent,
    requestRefresh,
    tenantConfig
  };

  return (
    <EmbeddedAppContext.Provider value={contextValue}>
      {children}
    </EmbeddedAppContext.Provider>
  );
};

export default EmbeddedAppProvider;